import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ArrowRight, Zap, Brain, Code, Database, Users, Globe, TrendingUp, CheckCircle, Calendar } from 'lucide-react';
import { Link } from "react-router-dom";
import tulioIcon from './assets/tulio.png'
import Navigation from './Navigation';

const ServicesPage = () => {
  const services = [
    {
      id: 'full-product-development',
      title: "Full Product Development",
      description: "End-to-end product development from vision to reality. We handle design, engineering, deployment, and scaling for ambitious organizations.",
      icon: Code,
      features: ["Vision & Planning", "Design & UX/UI", "Engineering & Deployment", "Scaling & Maintenance"],
      color: "from-emerald-500 to-teal-600",
      pricing: "Starting from $50k",
      timeline: "12-24 weeks"
    },
    {
      id: 'ai-consulting',
      title: "AI Consulting & Agent Development", 
      description: "Transform your business with intelligent automation, custom AI agents, and workflow optimization that delivers measurable ROI.",
      icon: Brain,
      features: ["Workflow AI Integration", "Custom AI Agents", "Legacy System Enhancement", "MLOps & Monitoring"],
      color: "from-cyan-500 to-blue-600",
      pricing: "Starting from $25k",
      timeline: "6-12 weeks"
    },
    {
      id: 'technology-consulting',
      title: "Technology Consulting & Integration",
      description: "Solve complex technical challenges with architectural guidance, system integrations, and performance optimization.",
      icon: Zap,
      features: ["Stack Evaluation & Reengineering", "System Integrations", "Performance & Security", "Code Audits"],
      color: "from-orange-500 to-red-600",
      pricing: "Starting from $15k",
      timeline: "4-8 weeks"
    },
    {
      id: 'staff-augmentation',
      title: "Staff Augmentation & Team Scaling",
      description: "Scale your team with our global engineering talent. Flexible capacity with seamless integration into your workflows.",
      icon: Users,
      features: ["Remote & Onsite Engineers", "Flexible Scaling", "Cost Advantages", "Cultural Integration"],
      color: "from-violet-500 to-purple-600",
      pricing: "From $3k/month per engineer",
      timeline: "2-4 weeks onboarding"
    },
    {
      id: 'data-engineering',
      title: "Data Engineering & ML Operations",
      description: "Transform data into actionable insights with advanced analytics, ML models, and scalable data platforms.",
      icon: Database,
      features: ["ETL Pipelines & Data Lakes", "Analytics Dashboards", "ML Model Deployment", "Data Governance"],
      color: "from-teal-500 to-green-600",
      pricing: "Starting from $30k",
      timeline: "8-16 weeks"
    },
    {
      id: 'growth-support',
      title: "Growth & Capital Support",
      description: "Beyond technology - helping with grants, financing, fundraising, and strategic growth initiatives.",
      icon: TrendingUp,
      features: ["Grant Consulting", "Fundraising Support", "Strategic Partnerships", "Market Entry"],
      color: "from-indigo-500 to-blue-600",
      pricing: "Success-based pricing",
      timeline: "Varies by scope"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
             <Link to="/" className="flex items-center space-x-3">
            <img 
  src={tulioIcon} 
  alt="Tulio Labs" 
  className="h-12 w-auto" 
/>

            </Link>
            
            <Navigation />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="mb-6 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Our Services</Badge>
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            Comprehensive Technology Solutions
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-12">
            From AI-powered products to enterprise transformations, we deliver end-to-end technology solutions that drive measurable business growth.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => window.open('https://calendly.com/admin-tuliolabs/30min', '_blank')}
            >
              <Calendar className="w-5 h-5 mr-2" />
              Schedule Consultation
            </Button>
            <Link to="/case-studies">
              <Button size="lg" variant="outline">
                View Case Studies
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <Card key={service.id} className="group hover:shadow-2xl transition-all duration-300 border-slate-200 hover:border-emerald-200 h-full">
                  <CardContent className="p-8 flex flex-col h-full">
                    <div className="mb-6">
                      <div className={`w-12 h-12 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-4">{service.title}</h3>
                      <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>
                    </div>

                    <div className="flex-1">
                      <div className="mb-6">
                        <h4 className="font-semibold text-slate-800 mb-3">What's Included:</h4>
                        <ul className="space-y-2">
                          {service.features.map((feature, idx) => (
                            <li key={idx} className="flex items-center text-sm text-slate-600">
                              <CheckCircle className="w-4 h-4 text-emerald-500 mr-2 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="border-t pt-6 mt-auto">
                        <div className="flex justify-between items-center mb-4 text-sm text-slate-600">
                          <span><strong>Investment:</strong> {service.pricing}</span>
                          <span><strong>Timeline:</strong> {service.timeline}</span>
                        </div>
                        
                        <Link to={`/services/${service.id}`}>
                          <Button className="w-full bg-slate-900 hover:bg-slate-800 group">
                            Learn More
                            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Business?</h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Schedule a free consultation to discuss your project and explore how we can help you achieve your technology goals.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-emerald-600 hover:bg-emerald-700 px-8 py-4"
              onClick={() => window.open('https://calendly.com/admin-tuliolabs/30min', '_blank')}
            >
              <Calendar className="w-5 h-5 mr-2" />
              Schedule Free Consultation
            </Button>
            <Link to="/#contact">
              <Button size="lg" variant="outline" className="border-slate-400 text-white hover:bg-white/10 px-8 py-4">
                Get Custom Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <Link to="/" className="flex items-center space-x-3 mb-6 md:mb-0">
             <img 
  src={tulioIcon} 
  alt="Tulio Labs" 
  className="h-12 w-auto" 
/>

            </Link>
            
            <div className="flex items-center space-x-8 text-sm text-slate-400">
              <Link to="/services" className="hover:text-white transition-colors">Services</Link>
              <Link to="/case-studies" className="hover:text-white transition-colors">Case Studies</Link>
              <span>© 2025 Tulio Labs</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServicesPage;